/**
 * SL1 reservation import — shared vocabulary (LOCKER-382, children A + B + D)
 * ==========================================================================
 *
 * **"SL1" here means the smart-locker feature in `mobilus-webapp`, NOT SST API
 * v1.** The two are related only in that the webapp called the SirSteward v1
 * API. Everything under `src/sstv2-evidence/**` and every `sl1_locker_id` uses
 * the *other* sense; nothing in this file does. Getting that backwards is the
 * single most likely way to read this module wrong, so it is the first line.
 *
 * Two entities live here, and they answer different questions:
 *
 * - `sl1ImportJob` — one record per RUN (a recon today, an apply once child E
 *   exists). Append-only. "What happened, when, against which build."
 * - `sl1ImportFleet` — one record per WEBAPP PROVIDER, refreshed by
 *   `sl1Recon.ts --scan`. A snapshot, not a live query. "Where does the whole
 *   operation stand right now."
 *
 * ## Why the fleet record is a snapshot and says so
 *
 * The alternative is a grouped count over `orders` — the biggest table in
 * `mobilus2` — on every page load of an operator-refreshed screen. The cost of
 * the snapshot is staleness, which is why `scannedAt` is on the FACE of the UI
 * and drift is computed (`sl1Rows - sl1RowsAtLastRecon`) rather than assumed
 * zero. SL1 is still live: `OrderController.submit()` keeps writing locker rows
 * while an import runs, so a provider is never *finished*, only *current*.
 *
 * ## Why drift is per-provider and never averaged
 *
 * Folding drift into a fleet-wide aggregate is exactly how the fact that SL1 is
 * still writing gets lost — a single "97% imported" number reads as done. The
 * meter reports imported-of-found; the drift marker sits on the provider row.
 */

// ---------------------------------------------------------------------------
// Thing type MRNs
// ---------------------------------------------------------------------------

/** One record per recon/apply run. Append-only; the job id is the identity. */
export const Sl1ImportJobThingTypeMrn = "mrn:sl2:::sl1ImportJob";

/** One record per webapp provider, refreshed wholesale by `--scan`. */
export const Sl1ImportFleetThingTypeMrn = "mrn:sl2:::sl1ImportFleet";

/**
 * The DDB partition BOTH records live in.
 *
 * Always `"sa"`, never the imported-into tenant: these are super-admin operational
 * records about an estate-wide operation, and scoping them per tenant would make
 * the console's one-Query read a fan-out across every tenant. Mirrors
 * `sstv2MigrationEvidenceRun`, whose `tenantId` is the SA-global "sa" for the
 * same reason.
 */
export const SL1_IMPORT_SA_TENANT = "sa";

// ---------------------------------------------------------------------------
// Run kind + status
// ---------------------------------------------------------------------------

export const Sl1ImportJobKindList = ["recon", "apply"] as const;
export type Sl1ImportJobKind = (typeof Sl1ImportJobKindList)[number];

/**
 * A run's lifecycle.
 *
 * `NEEDS_REVIEW` and `BLOCKED` are separate rungs on purpose: the first says
 * *rows need a human decision before an apply is authorised*, the second says
 * *rows cannot be imported at all as they stand*. Collapsing them would leave an
 * operator unable to tell "look at this" from "fix the data first".
 *
 * `SUPERSEDED` exists so a re-run does not leave the old recon looking
 * authoritative — nothing else in the vocabulary can express "this was true and
 * is no longer the answer".
 */
export const Sl1ImportJobStatusList = [
  "PENDING",
  "RUNNING",
  "COMPLETED",
  "NEEDS_REVIEW",
  "BLOCKED",
  "PARTIAL",
  "FAILED",
  "SUPERSEDED",
] as const;
export type Sl1ImportJobStatus = (typeof Sl1ImportJobStatusList)[number];

/** Statuses in which a run is still moving. */
export const Sl1ImportActiveStatuses: ReadonlySet<Sl1ImportJobStatus> =
  new Set<Sl1ImportJobStatus>(["PENDING", "RUNNING"]);

export const isActiveSl1ImportStatus = (s: string): boolean =>
  Sl1ImportActiveStatuses.has(s as Sl1ImportJobStatus);

/** Statuses that mean the run produced nothing an apply may lean on. */
export const isUnusableSl1ImportStatus = (s: string): boolean =>
  s === "FAILED" || s === "SUPERSEDED";

// ---------------------------------------------------------------------------
// Per-row resolution + anomalies
// ---------------------------------------------------------------------------

/**
 * What the recon decided about ONE SL1 row — a statement about MATCHING, and
 * nothing else.
 *
 * `matched_fallback` is deliberately distinct from `matched_exact`: the exact
 * match is on `integrationOrderId` ↔ `externalOrderId`, and the fallback is the
 * composite (locker + door + client + created-at window). A fallback match is
 * good enough to skip an import and NOT good enough to enrich a row without a
 * human looking, so the two must never render as one number.
 *
 * ## Why `unmatched` and not `importable`
 *
 * The bucket used to be called `importable`, from a model in which a matched row
 * was one SL2 already had and the import therefore skipped. Once the `adopt-`
 * husks were understood to BE SL1 reservations, a matched row became one the
 * apply REPLACES — so both buckets are written and the name claimed something
 * false: on the measured provider it read "114 importable" for a run that writes
 * 4,274 rows. The recon grades matching; what gets written is the apply's own
 * vocabulary (`insert` / `replaceHusk` / `retireOrphanHusk`), and the two are
 * deliberately not the same words.
 */
export const Sl1ResolutionList = [
  "matched_exact",
  "matched_fallback",
  "unmatched",
  "ambiguous",
  "blocked",
] as const;
export type Sl1Resolution = (typeof Sl1ResolutionList)[number];

/**
 * The anomaly vocabulary of the ticket's §D-2 table, in its stated order.
 *
 * `noSstReservationIdentity` is an ADDITION made when the match key moved to the
 * SST v1 reservation identity (LOCKER-382 `D13`). Without it a row whose vendor
 * blob parses but carries no reservation id and no codes would classify
 * `unmatched` in silence — which is exactly the shape of the round-1 `F1.2`
 * defect, where an unmatchable row read as a new one. A row that could not be
 * matched must SAY it could not be matched.
 */
export const Sl1AnomalyCategoryList = [
  "vendorJsonUnparseable",
  "duplicateIntegrationOrderId",
  "createdAtMissingOrFuture",
  "noSstReservationIdentity",
  "ambiguousMatch",
  "noClientIdentity",
  "doorNotInSl2",
  "lockerNotInSl2",
] as const;
export type Sl1AnomalyCategory = (typeof Sl1AnomalyCategoryList)[number];

export type Sl1AnomalySeverity = "blocked" | "review";

/**
 * Category → severity, and it is the ONLY place that mapping lives.
 *
 * The recon uses it to classify rows, the console uses it to colour the anomaly
 * table, and child E will use it to refuse an import. Three copies of this table
 * would eventually disagree, and the visible symptom would be a console calling a
 * row unmatched while the apply refuses it.
 */
export const SL1_ANOMALY_SEVERITY: Record<
  Sl1AnomalyCategory,
  Sl1AnomalySeverity
> = {
  vendorJsonUnparseable: "blocked",
  // `review`, NOT `blocked` — re-graded by LOCKER-382 `N4` / `D13`.
  //
  // It was blocking because `smart_locker_data_integration_order_id` WAS the
  // match key, so a duplicate meant no stable key to import or match on. It is
  // not the key any more: `N4` established that the id and the reservation
  // Thing's `externalOrderId` are two different values, and the key is now the
  // SST v1 reservation identity out of the vendor blob (`D13`). A duplicate is
  // still worth an operator's eye — two orders naming one vendor order is a real
  // data problem — but it no longer stops the row from being matched or
  // imported, and leaving it `blocked` withheld 16 of 46 rows on QA provider 157
  // for a reason that had stopped applying.
  duplicateIntegrationOrderId: "review",
  createdAtMissingOrFuture: "blocked",
  noSstReservationIdentity: "review",
  ambiguousMatch: "review",
  noClientIdentity: "review",
  doorNotInSl2: "review",
  lockerNotInSl2: "review",
};

/** Every category that stops an import outright. */
export const SL1_BLOCKING_ANOMALIES: readonly Sl1AnomalyCategory[] =
  Sl1AnomalyCategoryList.filter(
    (c) => SL1_ANOMALY_SEVERITY[c] === "blocked",
  ) as Sl1AnomalyCategory[];

export const isBlockingAnomaly = (c: Sl1AnomalyCategory): boolean =>
  SL1_ANOMALY_SEVERITY[c] === "blocked";

/**
 * The resolution a row lands on given its anomalies and whether it matched.
 *
 * Shared FE/BE because the console re-derives nothing: a row's resolution is
 * written by the recon and only READ by the UI. This function is the recon's own
 * classifier, exported so the guard test can drive it without a database.
 *
 * Order matters and is the rule, not an implementation detail: **blocked beats
 * everything**. A row that both matches ambiguously and carries an unparseable
 * vendor blob is `blocked` — it is neither written nor a review item,
 * because there is nothing to review until the data is fixed.
 */
export const resolveSl1Row = (args: {
  anomalies: readonly Sl1AnomalyCategory[];
  exactMatchId?: string;
  fallbackMatchId?: string;
  candidateCount?: number;
}): Sl1Resolution => {
  const { anomalies, exactMatchId, fallbackMatchId, candidateCount } = args;
  if (anomalies.some(isBlockingAnomaly)) return "blocked";
  if (exactMatchId) return "matched_exact";
  if ((candidateCount ?? 0) > 1) return "ambiguous";
  if (fallbackMatchId) return "matched_fallback";
  return "unmatched";
};

// ---------------------------------------------------------------------------
// Counts
// ---------------------------------------------------------------------------

export type Sl1ImportCounts = {
  /** SL1 rows selected by the window + provider filter. */
  found: number;
  matchedExact: number;
  matchedFallback: number;
  /** Matched nothing in SL2 — the apply inserts these bare. */
  unmatched: number;
  /**
   * Matched an `adopt-` husk — the apply REPLACES these.
   *
   * A subset of `matchedExact + matchedFallback`, not a sixth disjoint bucket, so
   * it is not drawn as a bar segment. It exists because it is the number that was
   * invisible: without it the report's only statement about size was `unmatched`,
   * which on the measured provider was 114 of the 4,274 rows a run writes.
   */
  replacesHusk: number;
  review: number;
  blocked: number;
};

export const emptySl1ImportCounts = (): Sl1ImportCounts => ({
  found: 0,
  matchedExact: 0,
  matchedFallback: 0,
  unmatched: 0,
  replacesHusk: 0,
  review: 0,
  blocked: 0,
});

/**
 * How many rows an apply of this report would WRITE.
 *
 * The recon's own counters are all about matching, and no single one of them is
 * the size of the job. One function so the verdict, the plan's batch sizing and
 * the UI cannot each derive it differently.
 */
export const sl1RowsToWrite = (counts: Sl1ImportCounts): number =>
  counts.unmatched + counts.replacesHusk;

export const emptySl1AnomalyCounts = (): Record<Sl1AnomalyCategory, number> =>
  Sl1AnomalyCategoryList.reduce(
    (acc, c) => ({ ...acc, [c]: 0 }),
    {} as Record<Sl1AnomalyCategory, number>,
  );

/**
 * Tally a classified row set into the counts BOTH the Thing and the report carry.
 *
 * Computed once, in one place, and copied onto the record — §D-1's `counts` is
 * denormalised deliberately so the job list renders with zero report reads and
 * survives a report being lifecycled away. Two implementations of this tally
 * would produce a list whose numbers contradict the detail page it links to.
 *
 * `review` counts ROWS, not anomalies: a row with three review-severity anomalies
 * is one thing for a human to look at, and counting it three times would inflate
 * the queue the operator is trying to size.
 */
export const tallySl1Rows = (
  rows: readonly {
    resolution: Sl1Resolution;
    anomalies: readonly Sl1AnomalyCategory[];
    matchedHusk?: boolean;
  }[],
): Sl1ImportCounts => {
  const counts = emptySl1ImportCounts();
  counts.found = rows.length;
  for (const r of rows) {
    if (r.resolution === "matched_exact") counts.matchedExact += 1;
    else if (r.resolution === "matched_fallback") counts.matchedFallback += 1;
    else if (r.resolution === "unmatched") counts.unmatched += 1;
    else if (r.resolution === "blocked") counts.blocked += 1;
    if (r.matchedHusk) counts.replacesHusk += 1;
    if (
      r.resolution !== "blocked" &&
      r.anomalies.some((a) => !isBlockingAnomaly(a))
    ) {
      counts.review += 1;
    }
  }
  return counts;
};

export const tallySl1Anomalies = (
  rows: readonly { anomalies: readonly Sl1AnomalyCategory[] }[],
): Record<Sl1AnomalyCategory, number> => {
  const counts = emptySl1AnomalyCounts();
  for (const r of rows) for (const a of r.anomalies) counts[a] += 1;
  return counts;
};

// ---------------------------------------------------------------------------
// Per-provider status — what the overview's rightmost column says
// ---------------------------------------------------------------------------

/**
 * The six states a provider row can be in, in the order the operation walks
 * through them. `NEEDS_MAPPING` is FIRST because it precedes everything: a
 * provider with no SL2 tenant has nowhere to import to, so no other state can be
 * true of it yet.
 */
export const Sl1ProviderStateList = [
  "NEEDS_MAPPING",
  "NOT_STARTED",
  "NEEDS_REVIEW",
  "RESUME_IMPORT",
  "IMPORTED",
  "BLOCKED",
] as const;
export type Sl1ProviderState = (typeof Sl1ProviderStateList)[number];

/**
 * A provider's state, derived from the mapping PLUS its latest job — never from
 * either alone.
 *
 * The mapping comes first and short-circuits: **`sl2TenantId === null` is
 * `NEEDS_MAPPING` no matter what any job says.** The link exists —
 * `Tenant.mblsProviderId` equals `integration_configuration.tenant_id` (`N1`,
 * `F12`) — but it is populated on only 13 of 18 QA tenants, and one provider id is
 * claimed by two tenants. So `null` stays a normal, expected reading of a
 * correctly-scanned fleet rather than an error, and rendering it as a failure
 * would put red rows in front of an operator who has done nothing wrong.
 *
 * **Ambiguous is NOT a seventh state.** A provider claimed by two SL2 tenants also
 * lands on `NEEDS_MAPPING` — there is still no usable mapping — and the
 * distinction is carried by `sl2TenantAmbiguous` as a marker on the mapping cell.
 * The approved status vocabulary is these six, and a marker is the treatment drift
 * already uses for the same reason: it qualifies a cell rather than competing with
 * the column that says what to do next.
 *
 * Shared FE/BE so the console and any future reminder agree; the console is its
 * only caller today.
 */
export const sl1ProviderState = (args: {
  sl2TenantId: string | null | undefined;
  latestJobStatus?: string;
  latestJobKind?: Sl1ImportJobKind;
  imported?: number;
  remaining?: number;
}): Sl1ProviderState => {
  const { sl2TenantId, latestJobStatus, latestJobKind, imported, remaining } =
    args;
  if (!sl2TenantId) return "NEEDS_MAPPING";
  if (latestJobStatus === "BLOCKED" || latestJobStatus === "FAILED")
    return "BLOCKED";
  if (latestJobStatus === "NEEDS_REVIEW") return "NEEDS_REVIEW";
  // A PARTIAL apply is the one state with an obvious next action, and it is not
  // "review" — the cursor is sitting mid-batch and the operator resumes it.
  if (latestJobKind === "apply" && latestJobStatus === "PARTIAL")
    return "RESUME_IMPORT";
  if (!latestJobStatus) return "NOT_STARTED";
  if ((imported ?? 0) > 0 && (remaining ?? 0) === 0) return "IMPORTED";
  return latestJobKind === "apply" ? "RESUME_IMPORT" : "NOT_STARTED";
};

/**
 * Rows a provider still has to bring across.
 *
 * `sl1Rows - matchedInSl2 - imported`, floored at zero. Floored because the three
 * numbers are read at DIFFERENT instants — `sl1Rows` at the last `--scan`,
 * `matchedInSl2`/`imported` possibly later — and a negative "remaining" is an
 * artefact of that skew, not a fact about the data. Showing it would make the
 * operator distrust every other number on the row.
 */
export const sl1Remaining = (f: {
  sl1Rows: number;
  matchedInSl2: number;
  imported: number;
}): number => Math.max(0, f.sl1Rows - f.matchedInSl2 - f.imported);

/**
 * New SL1 rows written since this provider was last reconned, or `undefined` when
 * there is no recon to compare against.
 *
 * `undefined` and `0` are different readings and the UI must not merge them: zero
 * means *we looked and nothing moved*, undefined means *nobody has looked*.
 */
export const sl1Drift = (f: {
  sl1Rows: number;
  sl1RowsAtLastRecon?: number;
}): number | undefined =>
  f.sl1RowsAtLastRecon === undefined
    ? undefined
    : Math.max(0, f.sl1Rows - f.sl1RowsAtLastRecon);

// ---------------------------------------------------------------------------
// The recon window
// ---------------------------------------------------------------------------

/**
 * The `orders.date_created` range a run selected on. `null` means UNBOUNDED on
 * that end, and both null means the whole history.
 *
 * ## Why all-time is a represented STATE and not a very wide window
 *
 * Passing `--from 1900-01-01 --to 2100-01-01` looks equivalent and is not, twice
 * over:
 *
 *  1. **`orders.date_created` is nullable.** A row with a null date satisfies no
 *     range predicate at all, so a wide window silently DROPS SL1 rows that the
 *     import is supposed to preserve. Omitting the predicate keeps them. (QA has
 *     zero such rows today; the schema permits them, and "currently zero" is not
 *     a guarantee to build a one-way history rewrite on.)
 *  2. **The console would keep apologising for it.** `matchedInSl2` from a
 *     windowed recon cannot be compared against an all-time `sl1Rows`, so the
 *     fleet table qualifies both REMAINING and IN SL2 whenever a window was used.
 *     An all-time recon makes those numbers directly comparable — and only a
 *     window that SAYS it was unbounded lets the UI drop the caveat.
 */
export type Sl1ReconWindow = {
  fromDate: string | null;
  toDate: string | null;
};

/** Did this run look at the provider's whole history? */
export const sl1WindowIsAllTime = (w: Sl1ReconWindow | undefined): boolean =>
  !!w && w.fromDate === null && w.toDate === null;

/**
 * Are this window's counts narrower than the all-time figures they sit beside?
 *
 * The question the fleet table asks before qualifying a number. An ABSENT window
 * is not scoped either — that is a row which has only ever been `--scan`ned, and
 * whose `matchedInSl2` is a carried-forward zero rather than a measurement.
 */
export const sl1WindowScoped = (w: Sl1ReconWindow | undefined): boolean =>
  !!w && !sl1WindowIsAllTime(w);

// ---------------------------------------------------------------------------
// Report + ids
// ---------------------------------------------------------------------------

/** Bumped whenever the report's shape changes in a way a reader must notice. */
export const SL1_IMPORT_SCHEMA_VERSION = 2;

/**
 * The id stamped on every imported reservation by child E — recorded HERE, in
 * shared code, before that child opens (this card's D9).
 *
 * `sl1-<jobId>-<orderId>`: free, immutable, reaches OLAP with no schema change,
 * gives a one-predicate rollback target, and does **not** collide with the
 * `adopt-%` exclusion in `excludeAdoptedReservations.ts` — so imported rows stay
 * VISIBLE in client-facing reports, which is the entire point of the ticket.
 *
 * Exported and asserted in the guard test so the contract cannot drift into child
 * E as a re-invented string.
 */
export const sl1ImportedReservationId = (jobId: string, orderId: number) =>
  `sl1-${jobId}-${orderId}`;

/** `true` when an id was minted by an SL1 import. The rollback predicate. */
export const isSl1ImportedReservationId = (id: string): boolean =>
  id.startsWith("sl1-");

/**
 * A job id an operator can read and a table sorts correctly by.
 *
 * `RCN-YYYYMMDD-NNN` / `APL-YYYYMMDD-NNN`. The prefix is load-bearing in the
 * console: it is what lets a reader tell a dry run from a write at a glance in a
 * list that mixes both, without reading the `kind` column.
 */
export const buildSl1JobId = (
  kind: Sl1ImportJobKind,
  date: string,
  seq: number,
  /**
   * The provider this run is about. **Part of the identity, not decoration.**
   *
   * Without it, `RCN-<date>-<seq>` is only unique within one provider — and the
   * sequence is counted per provider — so two providers' first recon on the same
   * day both mint `RCN-20260826-001`, build the same `sk`, and the second
   * `putItem` silently overwrites the first. The symptom is a run vanishing from
   * Job history while the other provider's fleet row still points at the id
   * (round-1 F1.1).
   */
  providerTenantId: string,
): string =>
  `${kind === "recon" ? "RCN" : "APL"}-${date.replace(/-/g, "").slice(0, 8)}-${sl1ProviderIdSegment(
    providerTenantId,
  )}-${String(seq).padStart(3, "0")}`;

/**
 * The provider segment of a job id: lower-cased, non-alphanumerics folded to `-`,
 * capped at 12 characters.
 *
 * Capped because a real SL2 tenant id is a 26-character ULID and a 40-character
 * job id is unreadable in a table and unquotable in a chat message. Twelve
 * characters of a ULID is ~62 bits of the timestamp-plus-randomness prefix —
 * ample to separate the handful of providers this operation covers, and the
 * `sk` is the FULL provider's id anyway via `gsi1pk`, so a collision here would
 * still need the same day AND the same sequence AND a 12-char prefix clash.
 */
export const sl1ProviderIdSegment = (providerTenantId: string): string =>
  providerTenantId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 12) || "unknown";

export const sl1JobKindFromId = (jobId: string): Sl1ImportJobKind | undefined =>
  jobId.startsWith("RCN-")
    ? "recon"
    : jobId.startsWith("APL-")
      ? "apply"
      : undefined;

/**
 * Where a report object lives.
 *
 * The same key names the local file and the S3 object, so an operator who has
 * seen one recognises the other and the sink is a change of DESTINATION rather
 * than of contract. See `sl1ReconSink.ts`.
 */
export const sl1ReportKey = (providerTenantId: string, jobId: string) =>
  `sl1-import/${providerTenantId}/${jobId}.json`;

// ---------------------------------------------------------------------------
// S3 objects (LOCKER-382 child C) — the same shape LOCKER-374 uses
// ---------------------------------------------------------------------------

/** The SSM parameter publishing the import bucket, written by the SAM template. */
export const SL1_IMPORT_BUCKET_SSM_PARAM = "/sl2/sl1-import-bucket-name";

/**
 * The three object families a run produces, and why they are separate.
 *
 * - `report`   — the verdict, counts, reconciliation ledger and anomaly table.
 *                Small. This is what the detail page opens with.
 * - `findings` — one row per SL1 row, with the `proposed` write. The bulk of the
 *                report and the reason it cannot live on the Thing.
 * - `snapshot` — the RAW `orders` rows exactly as read from `mobilus2`, before any
 *                classification. Kept because an import preserves each
 *                reservation's original creation date, and when somebody asks in
 *                eight months why a 2024 reservation exists in SL2, this is the
 *                only artefact that answers. It cannot be re-derived: the webapp
 *                rows move.
 *
 * Split rather than stored together for the reason `reservationSyncData` gives
 * about its own snapshots: the console must be able to render a whole report
 * without fetching data almost nobody opens.
 */
export const Sl1ImportObjectKindList = [
  "report",
  "findings",
  "snapshot",
] as const;
export type Sl1ImportObjectKind = (typeof Sl1ImportObjectKindList)[number];

/**
 * Default split threshold for one stored object, in bytes.
 *
 * 6 MB, matching `DEFAULT_MAX_OBJECT_BYTES` in `reservationSyncData.ts` — the same
 * number for the same reasons, and deliberately not a second opinion about it.
 * Nothing on the serving side caps this: parts are handed over as presigned URLs
 * the browser fetches directly, so API Gateway's own limit never applies. The real
 * cost of a bigger part is the single-row case, which the console bounds by
 * caching a fetched part instead of re-fetching per row.
 *
 * Behind a script flag (`--max-object-bytes`) because one pathological provider
 * should not need a code change.
 */
export const SL1_IMPORT_MAX_OBJECT_BYTES = 6 * 1024 * 1024;

/**
 * `sl1-import/<providerTenantId>/<jobId>/<kind>/part-0001.json`
 *
 * Zero-padded so a bucket listing sorts the way a human reads it, and `.json`
 * because these are audit objects somebody will `aws s3 cp … | jq` one day.
 * Uncompressed on purpose: at this volume compression saves pennies and costs
 * exactly that.
 *
 * Provider-scoped so a bucket listing groups by provider, which is the unit an
 * operator reasons about.
 */
export const sl1ObjectKey = (args: {
  providerTenantId: string;
  jobId: string;
  kind: Sl1ImportObjectKind;
  index: number;
}): string =>
  `sl1-import/${args.providerTenantId}/${args.jobId}/${args.kind}/part-${String(
    args.index + 1,
  ).padStart(4, "0")}.json`;

/**
 * Total stored bytes across a run's manifest — what the console's header shows.
 */
export const sl1TotalObjectBytes = (
  objects: { bytes: number }[] | undefined,
): number => (objects ?? []).reduce((a, o) => a + o.bytes, 0);

/**
 * Whether this run stored its raw SL1 snapshot at all.
 *
 * A run published with `--no-snapshot`, or one whose objects have aged out on the
 * lifecycle rule, has none — and the console must say so ONCE, up front, rather
 * than offering a per-row control that could never have worked. Same argument as
 * `runStoresSnapshots` next door.
 */
export const sl1StoresSnapshot = (
  objects: { kind: string }[] | undefined,
): boolean => (objects ?? []).some((o) => o.kind === "snapshot");

// ---------------------------------------------------------------------------
// Preserving the ORIGINAL creation date on an imported reservation (`N6`)
// ---------------------------------------------------------------------------

/**
 * Provenance stamped on a reservation Thing the SL1 import created.
 *
 * Kept on the row rather than only in the job record: eight months from now the
 * only honest answer to "why does this 2022 reservation exist in SL2" is the
 * order it was built from, and a job record can be deleted while the row remains.
 */
export type Sl1ImportOrigin = {
  /** The `sl1ImportJob` that wrote it. */
  jobId: string;
  /** `orders.id` in `mobilus_webapp`. */
  orderId: number;
  /** When the import ran — NOT the reservation's own date. */
  importedAt: string;
};

/**
 * May this create keep `inThing.createdAt` instead of being stamped `now`?
 *
 * PURE, so the rule is testable without DynamoDB, and deliberately narrow.
 *
 * ## Why an opt-in rather than honouring `createdAt` generally
 *
 * `reservationApi.createThings` hard-assigns `createdAt: new Date()` — the line
 * that would honour a caller's value is still there, commented out. Re-enabling it
 * would let every create path set its own creation date, including the public API,
 * which turns a field the whole audit trail rests on into caller input. This ticket
 * needs exactly one caller to backdate, so exactly one caller can.
 *
 * ## Three conditions, all required
 *
 * 1. A **service** principal. A human or a tenant API token can never backdate:
 *    an antedated reservation is a forged record, and the blast radius of getting
 *    that wrong is larger than this ticket.
 * 2. The `sl1Import` provenance marker, carrying the job and the order it came
 *    from — so a backdated row always says who backdated it and why.
 * 3. A `createdAt` that parses and is **not in the future**. The recon already
 *    blocks `createdAtMissingOrFuture`; this is the same rule enforced at the
 *    write, because the classifier's verdict and the writer's behaviour drifting
 *    apart is how a blocked row gets written anyway.
 *
 * Returns the date to use, or `null` meaning "stamp now and drop the claim" —
 * the same shape as the `sstReservationVersion` claim-drop beside it, and for the
 * same reason: persist the truth, never the request.
 */
export const sl1ImportPreservedCreatedAt = (args: {
  createdAt?: string | null;
  sl1Import?: Sl1ImportOrigin | null;
  principalType?: string;
  nowIso: string;
}): string | null => {
  const { createdAt, sl1Import, principalType, nowIso } = args;
  if (principalType !== "service") return null;
  if (!sl1Import || !sl1Import.jobId) return null;
  if (!createdAt) return null;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return null;
  if (t > Date.parse(nowIso)) return null;
  return new Date(t).toISOString();
};

// ---------------------------------------------------------------------------
// Folding SL1's customer data onto an imported row — with the vocabulary guarded
// ---------------------------------------------------------------------------

/** SL2's reservation languages. A value outside this set is DROPPED, not written. */
export const SL1_IMPORT_ALLOWED_LANGUAGES = ["en", "es", "fr"] as const;

/**
 * The SST statuses an imported `sstReservation` may carry.
 *
 * Measured on production: the live rows of the largest migrated provider are
 * `CLOSE` (11,442), `DELETED` (548) and `DELIVERED` (11); its husks are `CLOSE`
 * (4,315) and `DELETED` (221). SL1's own cached blob says `PENDING_DELIVERY`,
 * which appears NOWHERE in SL2 — it is the webapp's last-known state, frozen when
 * it stopped writing in Nov 2024, not the vendor's view.
 *
 * Writing it would put an uninterpretable value into a hard-parsing ORM and claim
 * 4,254 collected parcels are awaiting delivery. So the vendor object is taken
 * from the husk, and anything outside this set is refused at the write.
 */
export const SL1_IMPORT_ALLOWED_STATUSES = [
  "CONFIRMED",
  "DELIVERED",
  "RECEIVED",
  "CLOSE",
  "CLOSED",
  "EXPIRED",
  // `CANCEL` is the V1 spelling and `CANCELLED` the V2 one. Both are listed, and
  // the V1 one is the one that matters here: every imported row carries a V1
  // object, `SstReservationStatus` in `reservation.d.ts` spells it `CANCEL`, and
  // `STATUS_V2_TO_V1` in `sstv2Vocabulary.ts` translates V2 `CANCELLED` INTO it.
  //
  // It was missing until the fleet dry run found 2 rows carrying it on the
  // largest never-flipped provider — the list held only the V2 spelling, so the
  // guard was refusing the one form SL2 actually stores.
  "CANCEL",
  "CANCELLED",
  "DELETED",
] as const;

/**
 * `orders.amount` is dirty, and `amountToCollect` is money shown to a customer.
 *
 * Estate-wide the column ranges from **-54,696.40 to 2,000,000** with 11 negative
 * values and a positive mean of 86.63. Those extremes are data errors in a legacy
 * column nothing validated, and copying them onto a reservation would put a
 * two-million-dollar or negative collection amount on a real row.
 *
 * So: positive, finite, and under a ceiling, or omitted entirely. Omitted is safe —
 * 57% of SL1 rows carry 0 anyway and the field is optional — whereas a wrong number
 * is not.
 */
export const SL1_IMPORT_MAX_AMOUNT = 10_000;

export const sl1SafeAmount = (amount: number | null | undefined): number | null => {
  if (amount === null || amount === undefined) return null;
  if (!Number.isFinite(amount)) return null;
  if (amount <= 0) return null;
  if (amount > SL1_IMPORT_MAX_AMOUNT) return null;
  return Math.round(amount * 100) / 100;
};

/** `fr`/`en` pass; null, `FR`, `fr_CA` and anything else are dropped. */
export const sl1SafeLanguage = (language: string | null | undefined): string | null =>
  language && (SL1_IMPORT_ALLOWED_LANGUAGES as readonly string[]).includes(language)
    ? language
    : null;

/**
 * An email is written only if it could actually be one.
 *
 * 4,456 of the estate's non-null emails contain no `@`. A reservation row feeds
 * customer notification, so a value that cannot be an address is worse than none.
 */
export const sl1SafeEmail = (email: string | null | undefined): string | null => {
  const e = email?.trim();
  if (!e || !e.includes("@") || e.startsWith("@") || e.endsWith("@")) return null;
  return e;
};

/** Empty and whitespace-only become null, so a blank never overwrites a blank. */
export const sl1SafeText = (v: string | null | undefined): string | null => {
  const t = v?.trim();
  return t ? t : null;
};
